import { computed, ref, shallowRef, toValue } from "vue";
import { createVisitTypeFilterApi } from "#ce/apps/shared/filters/visit-type/api/visit-type-filter";

// The endpoint serves one flat list describing a two-level tree. A row whose
// `parent` is null is a top-level row — a visit-type category ("in-person") when
// the category has several named visit types, otherwise that single visit type
// standing in for its category. A row with a `parent` is one named visit type
// ("Office Visit") under its category's row. Every row's `value` is the
// comma-joined id list the `visit_types` search param carries.
//
// A row is *in scope* when the scoped fetch still lists its value. Both fetches
// derive an option's ids from the publishing domain rather than from the matching
// providers, so a value means the same thing in either response and scope
// membership is a set test.

/** The `value` of the row that clears the filter; no visit type can collide with it. */
export const ANY_VISIT_TYPE = "any";

function groupChildRows(rows) {
  const byParent = new Map();

  for (const row of rows) {
    if (row.parent == null) {
      continue;
    }
    const siblings = byParent.get(row.parent);
    if (siblings) {
      siblings.push(row);
    } else {
      byParent.set(row.parent, [row]);
    }
  }

  return byParent;
}

function hasScopeValues(scope) {
  return Object.values(scope ?? {}).some((value) => value != null && String(value).length > 0);
}

/**
 * Selection and option data for the Visit Type facet.
 *
 * @param {Object} [options]
 * @param {Object} [options.api] - Data source; defaults to `createVisitTypeFilterApi`.
 * @param {String} [options.url] - Endpoint override passed to the default api.
 * @param {String} [options.modelValue] - Initially selected value.
 * @param {String|Function} [options.anyLabel] - Title of the clearing row.
 * @param {Object|Function} [options.categoryLabels] - Per-category titles for top-level rows,
 *   keyed by the row's `category`. A category with no entry keeps the server's own text.
 */
export const useVisitTypeFilter = (options = {}) => {
  const api = options.api ?? createVisitTypeFilterApi({ url: options.url });

  const allRows = shallowRef([]);
  // Null while the facet is unscoped, which admits every row.
  const scopedRows = shallowRef(null);
  const pendingRequests = ref(0);
  const error = shallowRef(null);
  const selectedValue = ref(options.modelValue ?? undefined);

  const loading = computed(() => pendingRequests.value > 0);

  // Returns null when the request failed; a caller keeps the rows it already has.
  // `isCurrent` decides whether this request still speaks for the facet, so a
  // superseded request reports neither its rows nor its failure.
  async function fetchRows(params, isCurrent = () => true) {
    pendingRequests.value += 1;

    try {
      const rows = await api.all(params);

      if (isCurrent()) {
        error.value = null;
      }

      return rows;
    } catch (requestError) {
      if (isCurrent()) {
        error.value = requestError;
      }

      return null;
    } finally {
      pendingRequests.value -= 1;
    }
  }

  async function load() {
    const rows = await fetchRows();

    if (rows) {
      allRows.value = rows;
    }
  }

  // Counts every scope request so a slow earlier response cannot overwrite the
  // options of the search the visitor has since moved on to.
  let latestScopeRequest = 0;

  /**
   * Narrows the option list to the visit types the given provider-search filters
   * still match. A scope with no values restores the full list.
   *
   * @param {Object} [scope]
   * @returns {Promise<Boolean>} Whether the options were fetched. A scope with no values
   *   needs no request and reports true.
   */
  async function setScope(scope) {
    const request = (latestScopeRequest += 1);

    if (!hasScopeValues(scope)) {
      scopedRows.value = null;

      return true;
    }

    const isCurrent = () => request === latestScopeRequest;
    const rows = await fetchRows({ scope }, isCurrent);

    if (isCurrent() && rows) {
      scopedRows.value = rows;
    }

    return Boolean(rows);
  }

  const scopedValues = computed(() => (scopedRows.value ? new Set(scopedRows.value.map((row) => row.value)) : null));

  function isInScope(row) {
    return scopedValues.value == null || scopedValues.value.has(row.value) || row.value === selectedValue.value;
  }

  // The server hides a row it does not want offered, which the current selection
  // overrides so a restored deep link still names what it filters by.
  function isOffered(row) {
    return Boolean(row.visible) || row.value === selectedValue.value;
  }

  // The server groups a category's visit types under a category row only where their
  // filter names repeat, so a category can also reach the top level as several rows of
  // its own (an `in-person` category offering "Office Visit" and "Flu Shot").
  const topLevelRowsPerCategory = computed(() => {
    const counts = new Map();

    for (const row of allRows.value) {
      if (row.parent == null) {
        counts.set(row.category, (counts.get(row.category) ?? 0) + 1);
      }
    }

    return counts;
  });

  // A row takes its category's title only where it is the category's whole offering. Where a
  // category contributes several rows, each names the visit type it filters by.
  function titleFor(row) {
    if (row.parent != null || topLevelRowsPerCategory.value.get(row.category) > 1) {
      return row.text;
    }

    return toValue(options.categoryLabels)?.[row.category] ?? row.text;
  }

  function buildNode(row, childRows = []) {
    const children = childRows.filter(isInScope);

    if (!isInScope(row) && children.length === 0) {
      return undefined;
    }

    const node = { title: titleFor(row), value: row.value };

    if (children.length > 0) {
      node.children = children.map((child) => ({ title: titleFor(child), value: child.value }));
      node.hasChildren = true;
    }

    return node;
  }

  const visitTypeNodes = computed(() => {
    const rows = allRows.value.filter(isOffered);
    const childRows = groupChildRows(rows);

    // A category row carries offered children even when the category itself is not
    // offered, so a selection the server has since hidden stays reachable in the menu.
    const isTopLevelRow = (row) => row.parent == null && (isOffered(row) || childRows.has(row.value));

    return allRows.value
      .filter(isTopLevelRow)
      .map((row) => buildNode(row, childRows.get(row.value)))
      .filter(Boolean);
  });

  // With no visit type to choose from there is nothing to clear either, and an empty list is
  // what lets the menu show its own message — a lone clearing row would stand in place of it.
  const models = computed(() => {
    if (visitTypeNodes.value.length === 0) {
      return [];
    }

    return [{ title: toValue(options.anyLabel), value: ANY_VISIT_TYPE }, ...visitTypeNodes.value];
  });

  const selectedRow = computed(() =>
    selectedValue.value == null ? undefined : allRows.value.find((row) => row.value === selectedValue.value),
  );

  /** The activator's value text; undefined while the filter is cleared. */
  const selectedText = computed(() => (selectedRow.value ? titleFor(selectedRow.value) : undefined));

  /** The row values `<sp-quantum-select :selected>` marks, with the clearing row standing in for no selection. */
  const selectedNodeValues = computed(() => [selectedValue.value ?? ANY_VISIT_TYPE]);

  /** Applies a row's selection, reading the clearing row as "no visit type". */
  function selectNode(nodeValue) {
    selectedValue.value = nodeValue === ANY_VISIT_TYPE || nodeValue == null ? undefined : nodeValue;
  }

  function clear() {
    selectedValue.value = undefined;
  }

  load();

  return {
    loading,
    error,
    models,
    selectedValue,
    selectedRow,
    selectedText,
    selectedNodeValues,

    load,
    setScope,
    selectNode,
    clear,
  };
};
